import React, { useState, useEffect } from "react";
import { 
  AiOutlineUser, 
  AiOutlineMail, 
  AiOutlinePhone, 
  AiOutlineLock,
  AiOutlineEye,
  AiOutlineEyeInvisible 
} from "react-icons/ai";
import "../styles/pages/Profile.css";
import { getProfile, updateProfile, changePassword } from "../components/services/api";

export default function Profile() {
  // Personal Details State
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [agentCode, setAgentCode] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [userRole, setUserRole] = useState("");

  // Toast
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Password State
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Show/Hide Password
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // Errors
  const [errors, setErrors] = useState({});

  // Load user data on mount
  useEffect(() => {
    const fetch = async () => {
      const res = await getProfile();
      console.log('Profile fetch result:', res);
      console.log('Profile data:', res?.data);

      // Prefer API data, but fallback to sessionStorage userDetails
      const sessionUser = (() => {
        try {
          return JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        } catch (e) { return {}; }
      })();
      console.log('Session user:', sessionUser);

      if (res && res.success && res.data) {
        const data = res.data;
        console.log('Setting profile fields:', {
          fullName: data.fullName,
          username: data.username,
          email: data.email,
          mobileNumber: data.mobileNumber
        });
        setFullName(data.fullName || data.name || sessionUser.fullName || "");
        setUsername(data.username || sessionUser.username || "");
        setAgentCode(data.employeeId || data.agentCode || sessionUser.employeeId || "");
        setEmail(data.email || sessionUser.email || "");
        setMobile(data.mobileNumber || data.mobile || sessionUser.mobileNumber || sessionUser.mobile || "");
        setUserRole((data.role || sessionUser.role || localStorage.getItem('role') || '').toLowerCase());
      } else if (Object.keys(sessionUser || {}).length) {
        console.log('Using session storage fallback');
        setFullName(sessionUser.fullName || sessionUser.name || "");
        setUsername(sessionUser.username || "");
        setAgentCode(sessionUser.employeeId || sessionUser.agentCode || "");
        setEmail(sessionUser.email || "");
        setMobile(sessionUser.mobileNumber || sessionUser.mobile || "");
        setUserRole((sessionUser.role || localStorage.getItem('role') || '').toLowerCase());
        setToast({ show: true, message: 'Using cached profile data', type: 'info' });
      } else {
        console.error('Failed to load profile:', res);
        setToast({ show: true, message: res?.error || 'Failed to load profile', type: 'danger' });
      }
    };
    fetch();
  }, []);

  // Toast auto-hide
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const validateProfile = () => {
    const err = {};
    if (!fullName.trim()) err.fullName = "Full name is required";
    if (!email.trim()) err.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) err.email = "Email is invalid";
    if (!mobile.trim()) err.mobile = "Mobile number is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const validatePassword = () => {
    const err = {};
    if (!oldPassword) err.oldPassword = "Current password is required";
    if (!newPassword) err.newPassword = "New password is required";
    else if (newPassword.length < 6) err.newPassword = "Password must be at least 6 characters";
    if (newPassword !== confirmPassword) err.confirmPassword = "Passwords do not match";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;

    const payload = {
      email,
      mobileNumber: mobile,
      ...(isCoordinateAgent ? {} : { fullName }),
    };

    const res = await updateProfile(payload);
    if (res.success) {
      setToast({ show: true, message: 'Successfully updated the profile', type: 'success' });
    } else {
      setToast({ show: true, message: res.error || 'Update failed', type: 'danger' });
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    const res = await changePassword({ oldPassword, newPassword });
    if (res.success) {
      setToast({ show: true, message: 'Password changed successfully', type: 'success' });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      setToast({ show: true, message: res.error || 'Password change failed', type: 'danger' });
    }
  };

  const isCoordinateAgent = userRole === 'subadmin' || userRole === 'coordinateagent' || userRole === 'coordinate_agent' || userRole === 'coordinate agent';

  return (
    <div className="profile-security-container">
      <div className="profile-security-header">
        <h2>Profile & Security</h2>
        <p className="text-muted">Manage your personal information and security settings</p>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className={`profile-toast profile-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <div className="profile-toast-progress" />
        </div>
      )}

      {/* Personal Details Card */}
      <div className="profile-card shadow-sm">
        <div className="profile-card-header">
          <div className="profile-icon-circle bg-primary text-white">
            <AiOutlineUser size={20} />
          </div>
          <div>
            <h5 className="mb-0">Personal Details</h5>
            <small className="text-muted">View or update your personal information</small>
          </div>
        </div>

        <div className="profile-card-body">
          <form onSubmit={handleUpdateProfile}>
            {/* Full Name */}
            <div className="profile-form-group">
              <label className="profile-label">
                <AiOutlineUser className="profile-input-icon" />
                Full Name
              </label>
              <input
                type="text"
                className={`profile-input ${errors.fullName ? "is-invalid" : ""} ${isCoordinateAgent ? "profile-input-disabled" : ""}`}
                placeholder="Enter your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isCoordinateAgent}
              />
              {isCoordinateAgent && <small className="text-muted">Full name cannot be changed</small>}
              {errors.fullName && <small className="profile-error-text">{errors.fullName}</small>}
            </div>

            {/* Username (Read-only) */}
            <div className="profile-form-group">
              <label className="profile-label">
                <AiOutlineUser className="profile-input-icon" />
                Username
              </label>
              <input
                type="text"
                className="profile-input profile-input-disabled"
                placeholder="Username"
                value={username}
                disabled
              />
              <small className="text-muted">Username cannot be changed</small>
            </div>

            <div className="profile-form-group">
              <label className="profile-label">
                <AiOutlineUser className="profile-input-icon" />
                Agent Code
              </label>
              <input
                type="text"
                className="profile-input profile-input-disabled"
                placeholder="Agent Code"
                value={agentCode}
                disabled
              />
              <small className="text-muted">Agent code is system generated</small>
            </div>

            {/* Email */}
            <div className="profile-form-group">
              <label className="profile-label">
                <AiOutlineMail className="profile-input-icon" />
                Email
              </label>
              <input
                type="email"
                className={`profile-input ${errors.email ? "is-invalid" : ""}`}
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && <small className="profile-error-text">{errors.email}</small>}
            </div>

            {/* Mobile */}
            <div className="profile-form-group">
              <label className="profile-label">
                <AiOutlinePhone className="profile-input-icon" />
                Mobile
              </label>
              <input
                type="text"
                className={`profile-input ${errors.mobile ? "is-invalid" : ""}`}
                placeholder="Enter your mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
              {errors.mobile && <small className="profile-error-text">{errors.mobile}</small>}
            </div>

            <button type="submit" className="profile-btn-primary">
              Update Profile
            </button>
          </form>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="profile-card shadow-sm mt-4">
        <div className="profile-card-header">
          <div className="profile-icon-circle bg-info text-white">
            <AiOutlineLock size={20} />
          </div>
          <div>
            <h5 className="mb-0">Change Password</h5>
            <small className="text-muted">Update your password to keep your account secure</small>
          </div>
        </div>

        <div className="profile-card-body">
          <form onSubmit={handleUpdatePassword}>
            {/* Old Password */}
            <div className="profile-form-group">
              <label className="profile-label">Old Password</label>
              <div className="profile-password-wrapper">
                <input
                  type={showOldPass ? "text" : "password"}
                  className={`profile-input ${errors.oldPassword ? "is-invalid" : ""}`}
                  placeholder="Enter current password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="profile-eye-btn"
                  onClick={() => setShowOldPass(!showOldPass)}
                >
                  {showOldPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
              </div>
              {errors.oldPassword && <small className="profile-error-text">{errors.oldPassword}</small>}
            </div>

            {/* New Password */}
            <div className="profile-form-group">
              <label className="profile-label">New Password</label>
              <div className="profile-password-wrapper">
                <input
                  type={showNewPass ? "text" : "password"}
                  className={`profile-input ${errors.newPassword ? "is-invalid" : ""}`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="profile-eye-btn"
                  onClick={() => setShowNewPass(!showNewPass)}
                >
                  {showNewPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
              </div>
              {errors.newPassword && <small className="profile-error-text">{errors.newPassword}</small>}
            </div>

            {/* Confirm Password */}
            <div className="profile-form-group">
              <label className="profile-label">Confirm Password</label>
              <div className="profile-password-wrapper">
                <input
                  type={showConfirmPass ? "text" : "password"}
                  className={`profile-input ${errors.confirmPassword ? "is-invalid" : ""}`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="profile-eye-btn"
                  onClick={() => setShowConfirmPass(!showConfirmPass)}
                >
                  {showConfirmPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
              </div>
              {errors.confirmPassword && <small className="profile-error-text">{errors.confirmPassword}</small>}
            </div>

            <button type="submit" className="profile-btn-primary">
              Update Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}